import {Form, InputNumber, Modal, Select} from 'antd';
import PropTypes from 'prop-types';
import {useEffect} from 'react';

import DBModal from 'components/modal/modal';
import SubmitButton from 'components/Startup/components/SubmitButton';
import styles from './rewardBanks.module.scss';

/**
 * Create or top up a reward bank.
 *
 * One modal for both, and — crucially — one `Form.useForm()` instance bound to
 * exactly one `<Form>`. The screen used to render two `<Form form={form}>`
 * elements, one per modal, sharing a single instance. antd binds the callbacks
 * of whichever renders last, so once the Create modal had been opened, the
 * Edit modal's submit ran Create's validation instead: it failed on
 * "Currency is required" inside the closed modal, and the admin saw the Update
 * button do nothing at all. That is the "sometimes it does nothing" — the
 * condition was simply whether Create had been opened first.
 *
 * Mounting one form at a time makes that class of bug impossible rather than
 * merely fixed.
 */
const RewardBankModal = ({
  isOpen,
  mode,
  kind,
  record,
  options,
  currencyOptions,
  optionsLoading,
  saving,
  onClose,
  onSubmit,
}) => {
  const [form] = Form.useForm();
  const isEdit = mode === 'EDIT';
  const isCash = kind === 'CASH';
  const amountLabel = isEdit
    ? isCash
      ? 'Amount to add'
      : 'Units to add'
    : isCash
    ? 'Amount'
    : 'Units';
  const currentBalance = Number(
    (isCash ? record?.amountDeposited : record?.purchasedUnits) ?? 0,
  );

  // Cleared on open, never seeded with the current balance. `amountDeposited`
  // on the update mutation is ADDED to what the bank already holds — it does
  // not replace it. Pre-filling the box with the balance would mean an admin
  // opening a bank and pressing save doubles it, silently.
  useEffect(() => {
    if (!isOpen) return;
    form.resetFields();
  }, [isOpen, record, form]);

  const handleFinish = values => {
    const amount = Number(values?.amount);
    const unit = isCash ? record?.currency ?? '' : 'units';
    Modal.confirm({
      title: isEdit
        ? `Add to this ${isCash ? 'cash' : 'stock'} account?`
        : `Create a new ${isCash ? 'cash' : 'stock'} account?`,
      // Spells out the arithmetic, because the amount is added rather than
      // set. The old screen said nothing at all, so there was no way to
      // notice that "update" actually meant "top up".
      content: isEdit
        ? `${amount.toLocaleString('en-US')} ${unit} will be added to ${
            record?.label
          }, taking it from ${currentBalance.toLocaleString('en-US')} to ${(
            currentBalance + amount
          ).toLocaleString('en-US')}.`
        : `A new account will be opened with ${amount.toLocaleString(
            'en-US',
          )} ${isCash ? values?.currency ?? '' : 'units'}.`,
      okText: isEdit ? 'Add' : 'Create',
      onOk: () => onSubmit({...values, amount}),
    });
  };

  return (
    <DBModal
      isOpen={isOpen}
      handleClose={onClose}
      width={460}
      content={
        <div className={styles.modal}>
          <h2 className={styles.modalTitle}>
            {isEdit
              ? `Top up ${record?.label ?? 'account'}`
              : `Create ${isCash ? 'cash' : 'stock'} account`}
          </h2>
          <p className={styles.modalHint}>
            {isEdit ? (
              <>
                Currently holding{' '}
                <strong>{currentBalance.toLocaleString('en-US')}</strong>. What
                you enter is <strong>added</strong> to that, not a new total.
              </>
            ) : (
              'The bank starts with this balance and is drawn down as rewards go out.'
            )}
          </p>

          <Form form={form} layout="vertical" onFinish={handleFinish}>
            {!isEdit && (
              <Form.Item
                name={isCash ? 'currency' : 'instrumentId'}
                label={isCash ? 'Currency' : 'Instrument'}
                rules={[
                  {
                    required: true,
                    message: `${
                      isCash ? 'Currency' : 'Instrument'
                    } is required`,
                  },
                ]}>
                <Select
                  showSearch
                  optionFilterProp="label"
                  loading={optionsLoading}
                  placeholder={
                    isCash ? 'Pick a currency' : 'Search by ticker or name'
                  }
                  options={options}
                />
              </Form.Item>
            )}

            {!isEdit && !isCash && (
              <Form.Item
                name="currency"
                label="Currency"
                rules={[{required: true, message: 'Currency is required'}]}>
                <Select
                  placeholder="Pick a currency"
                  options={currencyOptions}
                />
              </Form.Item>
            )}

            {!isCash && (
              <Form.Item
                name="price"
                label="Unit price"
                rules={[{required: true, message: 'Unit price is required'}]}>
                <InputNumber
                  className={styles.numberInput}
                  min={0}
                  placeholder="0"
                />
              </Form.Item>
            )}

            <Form.Item
              name="amount"
              label={amountLabel}
              rules={[
                {required: true, message: `${amountLabel} is required`},
                {
                  // The service types both amounts as Int. A decimal used to
                  // be accepted by the form, rejected by the server, and then
                  // swallowed — the response came back without data and the
                  // handler destructured it, so the error never surfaced.
                  validator: (_, value) =>
                    value === undefined ||
                    value === null ||
                    Number.isInteger(Number(value))
                      ? Promise.resolve()
                      : Promise.reject(
                          new Error(`${amountLabel} must be a whole number`),
                        ),
                },
              ]}>
              <InputNumber
                className={styles.numberInput}
                min={0}
                step={1}
                placeholder="0"
                // Thousands separators while typing: these are balances in the
                // tens of thousands and a bare run of digits is easy to
                // misread by an order of magnitude.
                formatter={value =>
                  `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                }
                parser={value => `${value}`.replace(/,/g, '')}
              />
            </Form.Item>

            <SubmitButton
              loadingState={saving}
              label={isEdit ? 'Update account' : 'Create account'}
            />
          </Form>
        </div>
      }
    />
  );
};

RewardBankModal.propTypes = {
  isOpen: PropTypes.bool,
  mode: PropTypes.oneOf(['CREATE', 'EDIT']),
  kind: PropTypes.oneOf(['CASH', 'STOCK']),
  record: PropTypes.object,
  options: PropTypes.array,
  currencyOptions: PropTypes.array,
  optionsLoading: PropTypes.bool,
  saving: PropTypes.bool,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
};

export default RewardBankModal;
